/**
 * Rafraîchissement des panneaux liés à la progression.
 *
 * Portage de `LiveHudController` et `MarkerAlertHudController`. Quatre
 * panneaux partagent un même tick : indicateur kilométrique, roadbook, profil
 * d'altitude et — à sa propre cadence — radar d'alerte.
 *
 * Le calcul de position accrochée est fait **une fois** par tick et redistribué :
 * c'est l'opération coûteuse, la refaire par panneau quadruplerait le coût.
 */

import { lowerBound } from '../core/geo'
import type { Translator } from '../i18n'
import {
  alertPreviewEntries,
  demoRoadbookEntries,
  DEMO_PROFILE_POINTS,
  kmIndicatorPreview,
  PREVIEW_POSITION_KM,
  PREVIEW_PROFILE_POINTS,
  profilePreviewSeries,
  roadbookPreviewEntries,
} from './hudPreview'
import type { NavigationController } from '../core/navigation'
import type { PreferencesStore } from '../core/preferences'
import type { SnappedPositionTracker } from '../core/snappedPositionTracker'
import type { GpxTrack, RoadbookEntry } from '../core/track'
import { createCustomMarker } from '../core/types'
import type { Shell } from './shell'

/** Cadence du tick partagé, en millisecondes. */
const TICK_INTERVAL_MS = 5000

/**
 * Cadence dégradée pour les très longues traces.
 *
 * Au-delà de 300 km, la projection devient assez coûteuse pour qu'un
 * rafraîchissement toutes les cinq secondes se ressente sur la fluidité.
 */
const LONG_TRACK_TICK_INTERVAL_MS = 10_000
const LONG_TRACK_THRESHOLD_M = 300_000

/** Cadence propre au radar d'alerte, plus rapide car c'est une alerte. */
const ALERT_TICK_INTERVAL_MS = 1000

/** Délai avant effacement du radar quand plus aucun marqueur n'est en vue. */
const ALERT_FADE_DELAY_MS = 3000

/**
 * Tolérance au-delà de l'arrivée avant de masquer l'indicateur kilométrique.
 *
 * Sans marge, l'indicateur clignoterait à l'approche exacte de la ligne.
 */
const FINISH_TOLERANCE_KM = 0.05

/** Symbole des marqueurs de départ et d'arrivée ajoutés au radar d'alerte. */
const FLAG_SYMBOL = '🏁'

/** Panneaux susceptibles d'être montrés en aperçu. */
export type PreviewHud = 'km' | 'roadbook' | 'alert' | 'profile'

export class LiveHudController {
  /**
   * Le suivi est-il actif ?
   *
   * `start` et `stop` suivent l'activation du suivi de position, mais
   * `refresh` est appelé de partout ailleurs — une trace modifiée, un import,
   * une rotation d'écran, la sortie d'un aperçu. Sans cette mémoire, chacun de
   * ces appels rallumait les panneaux dès que la position se trouvait accrochée
   * à une trace, suivi éteint : l'APK ne les montre qu'en suivi.
   */
  #running = false

  #tickTimer: number | null = null
  #alertTimer: number | null = null
  #alertEmptySince: number | null = null

  /**
   * Panneaux actuellement en aperçu.
   *
   * Le tick partagé continue de tourner pour les autres : un panneau en aperçu
   * ne doit ni être rafraîchi avec de vraies données, ni être masqué par la
   * branche « désactivé ». C'est le rôle des quatre `*TestMode` d'Android,
   * rassemblés ici en un ensemble.
   */
  readonly #previewed = new Set<PreviewHud>()

  #previewBanner: HTMLElement | null = null

  constructor(
    private readonly shell: Shell,
    private readonly prefs: PreferencesStore,
    private readonly navigation: NavigationController,
    private readonly tracker: SnappedPositionTracker,
    private readonly t: Translator,
  ) {}

  start(): void {
    this.stop()
    this.#running = true
    this.#scheduleTick()

    this.#alertTimer = window.setInterval(
      () => this.#refreshMarkerAlert(),
      ALERT_TICK_INTERVAL_MS,
    )

    // Premier rendu immédiat : attendre le tick laisserait les panneaux vides
    // plusieurs secondes après l'activation du suivi.
    this.refresh()
  }

  stop(): void {
    this.#running = false

    // L'indice de progression part avec le suivi, comme le fait la branche
    // IDLE de `LiveHudController` (`snappedPosition.lastKnownKm = null`) : au
    // prochain suivi, l'accrochage repart du repère enregistré pour la trace
    // plutôt que d'une progression que le trajet a pu rendre fausse — un
    // transfert en voiture, une reprise à un autre bout de la boucle.
    this.tracker.reset()

    if (this.#tickTimer !== null) {
      window.clearTimeout(this.#tickTimer)
      this.#tickTimer = null
    }
    if (this.#alertTimer !== null) {
      window.clearInterval(this.#alertTimer)
      this.#alertTimer = null
    }

    this.#hideAll()
  }

  #scheduleTick(): void {
    const track = this.navigation.snappedTrack
    const interval =
      track !== null && track.totalDistance > LONG_TRACK_THRESHOLD_M
        ? LONG_TRACK_TICK_INTERVAL_MS
        : TICK_INTERVAL_MS

    this.#tickTimer = window.setTimeout(() => {
      this.refresh()
      this.#scheduleTick()
    }, interval)
  }

  /** Recalcule et réaffiche l'ensemble des panneaux. */
  refresh(): void {
    const track = this.navigation.snappedTrack
    const rawDistance = this.navigation.curvilinearDistance

    const visible = hudsVisible({
      following: this.#running,
      snapped: track !== null,
      hasFix: this.navigation.hasFix,
      curvilinearDistance: rawDistance,
    })

    // La trace est reprise dans la condition pour le seul compilateur : la
    // décision l'a déjà écartée, il ne peut pas le déduire d'un booléen.
    if (!visible || track === null) {
      this.#hideAll()
      return
    }

    // La progression est réinjectée dans le suivi : c'est elle qui lèvera
    // l'ambiguïté au prochain relevé sur une boucle.
    this.tracker.fixPosition(track.id, rawDistance / 1000)

    this.#refreshKmIndicator(track, rawDistance)
    this.#refreshRoadbook(track, rawDistance)
    this.#refreshElevationProfile(track, rawDistance)
    this.#refreshMarkerAlert()
  }

  #hideAll(): void {
    if (!this.#previewed.has('km')) this.shell.kmIndicator.hidden = true
    if (!this.#previewed.has('roadbook')) this.shell.roadbook.hidden = true
    if (!this.#previewed.has('profile')) this.shell.profileHud.hidden = true
    if (!this.#previewed.has('alert')) this.shell.alertHud.hidden = true
  }

  /* -------------------------------------------------------------------
   * Indicateur kilométrique
   * ---------------------------------------------------------------- */

  #refreshKmIndicator(track: GpxTrack, rawDistance: number): void {
    if (this.#previewed.has('km')) return

    if (!this.prefs.get('kmIndicatorEnabled')) {
      this.shell.kmIndicator.hidden = true
      return
    }

    const currentKm = track.rawDistanceToMarkerKm(rawDistance)
    const totalKm = track.rawDistanceToMarkerKm(track.totalDistance)
    const remainingKm = totalKm - currentKm

    // Passé l'arrivée, l'indicateur n'a plus rien à indiquer.
    if (remainingKm < -FINISH_TOLERANCE_KM) {
      this.shell.kmIndicator.hidden = true
      return
    }

    this.shell.setKmIndicator(currentKm, remainingKm, totalKm)
  }

  /* -------------------------------------------------------------------
   * Roadbook
   * ---------------------------------------------------------------- */

  #refreshRoadbook(track: GpxTrack, rawDistance: number): void {
    if (this.#previewed.has('roadbook')) return

    if (!this.prefs.get('roadbookEnabled')) {
      this.shell.roadbook.hidden = true
      return
    }

    const landscape = window.matchMedia('(orientation: landscape)').matches
    const maxEntries = landscape
      ? this.prefs.get('roadbookMarkerCountLandscape')
      : this.prefs.get('roadbookMarkerCountPortrait')

    const entries = track.getRoadbookEntries(
      rawDistance,
      maxEntries,
      this.prefs.get('roadbookDisappearDistance'),
    )

    this.shell.setRoadbook(entries, this.prefs.get('roadbookTextSize'))
  }

  /* -------------------------------------------------------------------
   * Profil d'altitude
   * ---------------------------------------------------------------- */

  #refreshElevationProfile(track: GpxTrack, rawDistance: number): void {
    if (this.#previewed.has('profile')) return

    if (!this.prefs.get('elevationProfileHudEnabled') || !track.hasElevationData) {
      this.shell.profileHud.hidden = true
      return
    }

    this.#applyElevationProfile(
      track.profileDistances,
      track.profileElevations,
      rawDistance,
    )
  }

  /**
   * Pose une série d'altitudes dans le panneau, fenêtrée autour d'une position.
   *
   * Partagé par le tick et par l'aperçu : c'est ici que vit la découpe, et la
   * dupliquer côté aperçu ferait mentir la démonstration au premier
   * ajustement.
   */
  #applyElevationProfile(
    distances: readonly number[],
    elevations: readonly number[],
    currentDistance: number,
  ): void {
    if (distances.length < 2) {
      this.shell.profileHud.hidden = true
      return
    }

    const window_ = computeProfileWindow({
      currentDistance,
      trackEnd: distances[distances.length - 1]!,
      aheadMeters: this.prefs.get('elevationProfileHudRange') * 1000,
      behindMeters: this.prefs.get('elevationProfileHudRangeBehind') * 1000,
    })

    if (window_ === null) {
      this.shell.profileHud.hidden = true
      return
    }

    // Un point de débord de chaque côté : sans lui, la courbe s'arrêterait
    // net au bord du cadre au lieu de le traverser.
    const startIndex = Math.max(lowerBound(distances, window_.start) - 1, 0)
    const endIndex = Math.min(
      lowerBound(distances, window_.end) + 1,
      distances.length,
    )

    if (endIndex <= startIndex) {
      this.shell.profileHud.hidden = true
      return
    }

    this.shell.setElevationProfile(
      distances.slice(startIndex, endIndex),
      elevations.slice(startIndex, endIndex),
      window_.start,
      window_.end,
      window_.cursorFraction,
      true,
    )
  }

  /* -------------------------------------------------------------------
   * Radar d'alerte
   * ---------------------------------------------------------------- */

  #refreshMarkerAlert(): void {
    if (this.#previewed.has('alert')) return

    const track = this.navigation.snappedTrack
    const rawDistance = this.navigation.curvilinearDistance

    if (
      !this.prefs.get('markerAlertEnabled') ||
      track === null ||
      rawDistance < 0
    ) {
      this.shell.alertHud.hidden = true
      return
    }

    const radiusKm = this.prefs.get('markerAlertDistance')
    const entries = buildAlertEntries(track, rawDistance, radiusKm)

    if (entries.length === 0) {
      // Effacement différé : un radar qui disparaît dès le dernier marqueur
      // passé donnerait une impression de clignotement.
      this.#alertEmptySince ??= Date.now()

      if (Date.now() - this.#alertEmptySince > ALERT_FADE_DELAY_MS) {
        this.shell.alertHud.hidden = true
      }
      return
    }

    this.#alertEmptySince = null
    this.shell.setMarkerAlert(
      entries,
      radiusKm,
      this.prefs.get('markerAlertSize'),
      true,
    )
  }

  /* -------------------------------------------------------------------
   * Aperçus
   * ---------------------------------------------------------------- */

  /**
   * Montre un panneau — ou tous — garni de données fictives.
   *
   * Les réglages de ces panneaux ne se jugent pas dans l'abstrait : nombre de
   * marqueurs, taille du texte, portée du profil, distance d'alerte. Sans
   * aperçu, régler un curseur demande de sortir rouler.
   *
   * Tout se joue à la position fixe d'Android, la même pour les cinq entrées.
   */
  showPreview(kind: PreviewHud | 'all'): void {
    this.closePreview()

    if (kind === 'all') {
      this.#applyDemo(PREVIEW_POSITION_KM)
      this.#showBanner()
      return
    }

    if (kind === 'km') {
      this.#previewed.add('km')
      const { currentKm, remainingKm, totalKm } = kmIndicatorPreview()
      this.shell.setKmIndicator(currentKm, remainingKm, totalKm)
    } else if (kind === 'roadbook') {
      this.#previewed.add('roadbook')
      this.shell.setRoadbook(
        roadbookPreviewEntries(),
        this.prefs.get('roadbookTextSize'),
      )
    } else if (kind === 'alert') {
      this.#previewed.add('alert')
      this.shell.setMarkerAlert(
        alertPreviewEntries(),
        this.prefs.get('markerAlertDistance'),
        this.prefs.get('markerAlertSize'),
        true,
      )
    } else {
      this.#previewed.add('profile')
      const { distances, elevations } = profilePreviewSeries(
        PREVIEW_PROFILE_POINTS,
      )
      this.#applyElevationProfile(
        distances,
        elevations,
        PREVIEW_POSITION_KM * 1000,
      )
    }

    this.#showBanner()
  }

  /** Repose les quatre panneaux à une position donnée de la trace fictive. */
  #applyDemo(positionKm: number): void {
    for (const hud of ['km', 'roadbook', 'alert', 'profile'] as const) {
      this.#previewed.add(hud)
    }

    const { currentKm, remainingKm, totalKm } = kmIndicatorPreview(positionKm)
    this.shell.setKmIndicator(currentKm, remainingKm, totalKm)

    const landscape = window.matchMedia('(orientation: landscape)').matches
    const maxEntries = landscape
      ? this.prefs.get('roadbookMarkerCountLandscape')
      : this.prefs.get('roadbookMarkerCountPortrait')

    this.shell.setRoadbook(
      demoRoadbookEntries(
        positionKm,
        this.prefs.get('roadbookDisappearDistance'),
        maxEntries,
      ),
      this.prefs.get('roadbookTextSize'),
    )

    this.shell.setMarkerAlert(
      alertPreviewEntries(positionKm),
      this.prefs.get('markerAlertDistance'),
      this.prefs.get('markerAlertSize'),
      true,
    )

    const { distances, elevations } = profilePreviewSeries(DEMO_PROFILE_POINTS)
    this.#applyElevationProfile(distances, elevations, positionKm * 1000)
  }

  /** Ferme l'aperçu en cours et rend la main aux vraies données. */
  closePreview(): void {
    if (this.#previewed.size === 0 && this.#previewBanner === null) return

    this.#previewBanner?.remove()
    this.#previewBanner = null

    this.#previewed.clear()

    // Les panneaux sont masqués **avant** le rafraîchissement : sans cela, un
    // panneau qui n'a plus de données à montrer garderait celles de l'aperçu.
    this.shell.kmIndicator.hidden = true
    this.shell.roadbook.hidden = true
    this.shell.profileHud.hidden = true
    this.shell.alertHud.hidden = true

    this.refresh()
  }

  /**
   * Carte de sortie, au centre de l'écran.
   *
   * Reprise de `showPreviewBanner` : une carte au contenu ajusté, centrée sur
   * les deux axes, ne portant que le bouton de fermeture. Elle recouvre ce qui
   * se trouve au milieu de la carte — Android l'assume, et c'est la seule
   * sortie d'un aperçu : mieux vaut la trouver sans la chercher.
   */
  #showBanner(): void {
    const banner = document.createElement('div')
    banner.className = 'hud-preview'

    const close = document.createElement('button')
    close.type = 'button'
    close.className = 'button-tonal hud-preview__close'
    close.textContent = this.t.t('close_preview')
    close.addEventListener('click', () => this.closePreview())
    banner.append(close)

    this.shell.root.append(banner)
    this.#previewBanner = banner
  }
}

/**
 * Les panneaux de progression ont-ils lieu d'être affichés ?
 *
 * Quatre conditions, toutes nécessaires, dans l'ordre où elles se perdent :
 *
 *  - **le suivi est actif** : c'est la règle de l'APK, et la seule qui ne
 *    dépende pas de la position. Un cycliste qui consulte sa trace à l'arrêt,
 *    suivi éteint, n'a que faire d'un roadbook — et le montrer laisserait
 *    croire que la navigation tourne ;
 *  - **la position est accrochée à une trace** : hors trace, il n'y a ni point
 *    kilométrique, ni marqueurs à venir ;
 *  - **un relevé est arrivé** : sans lui, l'accrochage est celui d'avant ;
 *  - **l'abscisse curviligne est connue** : négative, elle signale un
 *    accrochage refusé plutôt qu'une position au départ.
 */
export function hudsVisible(state: {
  readonly following: boolean
  readonly snapped: boolean
  readonly hasFix: boolean
  readonly curvilinearDistance: number
}): boolean {
  return (
    state.following &&
    state.snapped &&
    state.hasFix &&
    state.curvilinearDistance >= 0
  )
}

export interface ProfileWindow {
  readonly start: number
  readonly end: number
  /** Position du curseur dans la fenêtre, entre 0 et 1. */
  readonly cursorFraction: number
}

/**
 * Fenêtre du profil d'altitude autour de la position courante.
 *
 * Le curseur est fixe au milieu de la fenêtre en marche normale, mais glisse
 * vers les bords au début et à la fin de la trace : le profil resterait sinon
 * à moitié vide, alors que le relief affichable existe.
 */
export function computeProfileWindow(input: {
  currentDistance: number
  trackEnd: number
  aheadMeters: number
  behindMeters: number
}): ProfileWindow | null {
  const { currentDistance, trackEnd, aheadMeters, behindMeters } = input

  const totalWindow = aheadMeters + behindMeters
  if (totalWindow <= 0 || trackEnd <= 0) return null

  let start = currentDistance - behindMeters
  let end = currentDistance + aheadMeters
  let cursorFraction = behindMeters / totalWindow

  if (start < 0) {
    start = 0
    end = Math.min(totalWindow, trackEnd)
    cursorFraction = end > 0 ? currentDistance / end : 0
  } else if (end > trackEnd) {
    end = trackEnd
    start = Math.max(trackEnd - totalWindow, 0)
    cursorFraction = end > start ? (currentDistance - start) / (end - start) : 1
  }

  return {
    start,
    end,
    cursorFraction: Math.min(Math.max(cursorFraction, 0), 1),
  }
}

/**
 * Entrées du radar d'alerte.
 *
 * Le départ et l'arrivée sont ajoutés aux marqueurs de l'utilisateur : ce sont
 * les deux repères qu'on veut voir venir même sans avoir posé de marqueur.
 */
export function buildAlertEntries(
  track: GpxTrack,
  rawDistance: number,
  radiusKm: number,
): RoadbookEntry[] {
  const entries = track
    .getRoadbookEntries(rawDistance, Number.MAX_SAFE_INTEGER, 1.0)
    .filter((entry) => entry.remainingKm <= radiusKm)

  const currentKm = track.rawDistanceToMarkerKm(rawDistance)
  const finishKm = track.rawDistanceToMarkerKm(track.totalDistance)

  const startRemaining = -currentKm
  if (startRemaining > -1.0 && startRemaining <= radiusKm) {
    entries.push({
      marker: createCustomMarker({
        type: 'Start',
        name: '',
        symbol: FLAG_SYMBOL,
        distanceKm: 0,
      }),
      remainingKm: startRemaining,
    })
  }

  const finishRemaining = finishKm - currentKm
  if (finishRemaining > -1.0 && finishRemaining <= radiusKm) {
    entries.push({
      marker: createCustomMarker({
        type: 'Finish',
        name: '',
        symbol: FLAG_SYMBOL,
        distanceKm: finishKm,
      }),
      remainingKm: finishRemaining,
    })
  }

  return entries.sort((a, b) => a.remainingKm - b.remainingKm)
}
